import { useEffect, useState, useMemo } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { API } from "../App";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import {
  Loader2, MapPin, Star, Clock, ChevronRight, Copy, RefreshCw, Info, Phone, ShieldCheck,
} from "lucide-react";

/**
 * Public magic-link comparison page. Loads the request + all quotes by
 * unguessable token. Same URL is reused for the life of the request so
 * drivers can reopen this over hours/days as quotes arrive.
 */

function fmtMoney(n) {
  if (n === null || n === undefined) return "—";
  return `$${Number(n).toFixed(2)}`;
}

export default function TokenQuoteView() {
  const { token } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null); // {request, quotes}
  const [sortBy, setSortBy] = useState("price"); // price | brand | date

  const load = async (isRefresh = false) => {
    if (isRefresh) setRefreshing(true);
    else setLoading(true);
    try {
      const res = await axios.get(`${API}/public/request-access/${encodeURIComponent(token)}`);
      setData(res.data);
      setError(null);
    } catch (e) {
      setError(e.response?.status === 404 ? "invalid" : "network");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    load();
    // Light auto-refresh every 60s so users get updates without a manual refresh
    const t = setInterval(() => load(true), 60000);
    return () => clearInterval(t);
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  const request = data?.request;
  const quotes = useMemo(() => {
    const list = [...(data?.quotes || [])];
    if (sortBy === "price") list.sort((a, b) => (a.total_price || 0) - (b.total_price || 0));
    else if (sortBy === "brand") list.sort((a, b) => (a.tyre_brand || "").localeCompare(b.tyre_brand || ""));
    else if (sortBy === "date") list.sort((a, b) => (a.fitment_estimate || "").localeCompare(b.fitment_estimate || ""));
    return list;
  }, [data, sortBy]);

  const copyLink = () => {
    navigator.clipboard.writeText(window.location.href).then(
      () => toast.success("Link copied"),
      () => toast.error("Copy failed"),
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin text-primary mx-auto mb-3" />
          <p className="text-sm text-muted-foreground">Loading your quotes...</p>
        </div>
      </div>
    );
  }

  if (error === "invalid") {
    return (
      <div className="min-h-screen flex items-center justify-center p-6 bg-background">
        <div className="max-w-md text-center space-y-4">
          <div className="w-14 h-14 rounded-full bg-muted mx-auto flex items-center justify-center">
            <Info className="h-7 w-7 text-muted-foreground" />
          </div>
          <h1 className="font-heading text-2xl font-bold" data-testid="token-error-title">Link not valid</h1>
          <p className="text-sm text-muted-foreground">
            This quote link is invalid or has expired. If you kept a copy of the original email, try opening the link from there. Otherwise, please submit a new request.
          </p>
          <div className="flex gap-2 justify-center pt-2">
            <Button onClick={() => navigate("/")} variant="outline">Home</Button>
            <Button onClick={() => navigate("/quote-request")} data-testid="token-new-request-btn">Start a new request</Button>
          </div>
        </div>
      </div>
    );
  }

  if (error === "network") {
    return (
      <div className="min-h-screen flex items-center justify-center p-6 bg-background">
        <div className="max-w-md text-center">
          <p className="mb-3">Couldn't load quotes. Please check your connection.</p>
          <Button onClick={() => load()}>Retry</Button>
        </div>
      </div>
    );
  }

  const req = request;
  const isExpired = req?.request_status === "expired";
  const address = req?.address?.full_address || "";
  const services = (req?.selected_services && req.selected_services.length > 0)
    ? req.selected_services.map(s => s.charAt(0).toUpperCase() + s.slice(1)).join(", ")
    : (req?.service_type || "").replace(/_/g, " ");

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b bg-background/95 backdrop-blur sticky top-0 z-30">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between gap-2">
          <Link to="/" className="font-heading font-bold text-lg text-primary" data-testid="token-home-link">
            TyreMatch
          </Link>
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={() => load(true)}
              disabled={refreshing}
              data-testid="token-refresh-btn"
            >
              {refreshing ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <RefreshCw className="h-3 w-3 mr-1" />}
              Refresh
            </Button>
            <Button size="sm" variant="secondary" onClick={copyLink} data-testid="token-copy-link-btn">
              <Copy className="h-3 w-3 mr-1" /> Copy link
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-6 space-y-6">
        {/* Request summary */}
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <Badge variant="outline" className="text-xs">
              {req.request_number || `#${req.request_id}`}
            </Badge>
            {isExpired ? (
              <Badge variant="destructive">Expired</Badge>
            ) : (
              <Badge className="bg-green-100 text-green-700 border-green-200">Active</Badge>
            )}
            {req.is_guest && (
              <Badge variant="secondary" className="text-xs">
                Guest — for {req.guest_name || "you"}
              </Badge>
            )}
          </div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold" data-testid="token-page-title">
            Your quotes
          </h1>
          <p className="text-sm text-muted-foreground flex items-center gap-1">
            <MapPin className="h-3.5 w-3.5" /> {address} · within {req.search_radius} km · {services}
          </p>
        </div>

        {/* Waiting state */}
        {quotes.length === 0 && !isExpired && (
          <Card className="border-dashed">
            <CardContent className="py-10 text-center">
              <Clock className="h-8 w-8 text-muted-foreground mx-auto mb-3" />
              <p className="text-base font-medium mb-1">No quotes yet — hang tight</p>
              <p className="text-sm text-muted-foreground">
                Nearby shops have been notified. Quotes may arrive over hours or days. This same link always shows the latest quotes.
              </p>
              <Button className="mt-4" onClick={() => load(true)} data-testid="token-check-again-btn">
                Check again
              </Button>
            </CardContent>
          </Card>
        )}

        {quotes.length > 0 && (
          <>
            {/* Controls */}
            <div className="flex items-center justify-between gap-2">
              <p className="text-sm text-muted-foreground">
                <strong>{quotes.length}</strong> quote{quotes.length === 1 ? "" : "s"} received
              </p>
              <div className="flex items-center gap-2">
                <span className="text-xs text-muted-foreground hidden sm:inline">Sort by</span>
                <Select value={sortBy} onValueChange={setSortBy}>
                  <SelectTrigger className="h-8 text-xs w-[140px]" data-testid="token-sort-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="price">Lowest price</SelectItem>
                    <SelectItem value="brand">Brand (A–Z)</SelectItem>
                    <SelectItem value="date">Earliest fitment</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Quotes stack — vertical for mobile-first comparability */}
            <div className="space-y-3">
              {quotes.map((q) => {
                const qty = req.quantity || 4;
                const perTyre = q.price_breakdown?.tyre_fitment && qty
                  ? Number(q.price_breakdown.tyre_fitment) / qty
                  : null;
                return (
                  <Card key={q.quote_id} className="overflow-hidden" data-testid={`token-quote-${q.quote_id}`}>
                    <CardHeader className="pb-3">
                      <div className="flex items-start justify-between gap-3 flex-wrap">
                        <div className="min-w-0 flex-1">
                          <CardTitle className="text-base sm:text-lg break-words leading-snug" data-testid={`token-shop-name-${q.quote_id}`}>
                            {q.shop_name || "Local shop"}
                          </CardTitle>
                          <CardDescription className="mt-1 flex items-center gap-3 text-xs">
                            {q.google_rating ? (
                              <span className="flex items-center gap-1">
                                <Star className="h-3.5 w-3.5 fill-amber-400 text-amber-400" />
                                {q.google_rating.toFixed(1)}
                                <span className="text-muted-foreground">({q.google_review_count || 0})</span>
                              </span>
                            ) : null}
                            {q.distance_km != null && (
                              <span className="flex items-center gap-1">
                                <MapPin className="h-3.5 w-3.5" /> {q.distance_km.toFixed(1)} km
                              </span>
                            )}
                          </CardDescription>
                        </div>
                        <div className="text-right shrink-0">
                          <p className="text-2xl sm:text-3xl font-bold text-primary" data-testid={`token-quote-total-${q.quote_id}`}>
                            {fmtMoney(q.total_price)}
                          </p>
                          <p className="text-xs text-muted-foreground">total incl. GST</p>
                          {perTyre !== null && (
                            <p className="text-xs text-muted-foreground mt-1">
                              ~{fmtMoney(perTyre)} per tyre fitted
                            </p>
                          )}
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent className="space-y-2 text-sm">
                      {(q.tyre_brand || q.tyre_model || q.tyre_size) && (
                        <p>
                          <span className="text-muted-foreground">Tyre:</span>{" "}
                          <strong>{[q.tyre_brand, q.tyre_model, q.tyre_size].filter(Boolean).join(" ")}</strong>
                        </p>
                      )}
                      {q.fitment_estimate && (
                        <p>
                          <span className="text-muted-foreground">Fitment:</span> {q.fitment_estimate}
                        </p>
                      )}
                      {q.notes && (
                        <p className="text-muted-foreground text-xs leading-relaxed">{q.notes}</p>
                      )}
                      <div className="flex items-center gap-2 pt-2 flex-wrap">
                        <Button
                          size="sm"
                          className="min-h-11"
                          onClick={() => navigate(`/customer/auth?tab=register&r=${encodeURIComponent(token)}&q=${encodeURIComponent(q.quote_id)}`)}
                          data-testid={`token-accept-quote-${q.quote_id}`}
                        >
                          Accept &amp; book
                          <ChevronRight className="h-4 w-4 ml-1" />
                        </Button>
                        <p className="text-xs text-muted-foreground flex items-center gap-1">
                          <ShieldCheck className="h-3.5 w-3.5" /> Create a quick account to book — no charge on TyreMatch
                        </p>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </>
        )}

        <Card className="bg-muted/30 border-dashed">
          <CardContent className="py-4 text-xs text-muted-foreground flex items-start gap-2">
            <Phone className="h-4 w-4 shrink-0 mt-0.5" />
            <p>
              This link is unique to your request. Keep it private — anyone with the link can view your quotes.
              Emails from each new shop will always use this same link.
            </p>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
